import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, User, Building, Phone, Mail, FileText, Settings2, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ExtendedLead } from '@/components/ClientDetails/types';  // Importe o tipo unificado
import AdicionarLead from '@/components/ClientDetails/AdicionarLead';
import ClientDetails from '@/components/ClientDetails/ClientDetails';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  isProdutoLeadCarregador,
  isProdutoLeadParceiroCrm,
  isProdutoLeadSemProduto,
  leadProdutoBadgeMeta,
} from '@/constants/leadsProduto';
import {
  DEFAULT_FUNIL_ETAPAS_API,
  DEFAULT_FUNIL_ETAPAS_PARCERIA_API,
  type CrmFunilEtapaApi,
} from '@/constants/crmFunilEtapas';
import { CRM_FUNIL_PARCERIA_PROSPECCAO, CRM_FUNIL_VENDAS, type CrmFunilTipo } from '@/constants/crmFunil';
import { toast } from '@/hooks/use-toast';

const formatPhone = (phone: string | undefined) => {
  if (!phone) return '';
  // Remove todos os caracteres não numéricos
  const cleaned = phone.replace(/\D/g, '');
  // Formata no padrão brasileiro (00) 0000-0000
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  }
  // Para números com 11 dígitos (celular com 9 dígitos)
  if (cleaned.length === 11) {
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
  }
  // Retorna o número original se não conseguir formatar
  return phone;
};

interface Column {
  id: string;
  title: string;
  leads: ExtendedLead[];
  categories?: string[];
  color: string;
  customBgColor?: string;
  /** Ordem no funil (1 = primeira coluna); usada para bloquear avanço sem produto definido. */
  ordem?: number;
}

type ColumnDef = Omit<Column, 'leads'>;

function apiEtapaToColDef(e: CrmFunilEtapaApi): ColumnDef {
  return {
    id: e.id,
    title: e.titulo,
    color: e.color,
    customBgColor: e.customBgColor,
    categories: e.categories,
    ordem: e.ordem,
  };
}

function newEtapaSlug(): string {
  const part =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
      : String(Date.now());
  return `etapa-${part.toLowerCase()}`;
}

function LeadProdutoBadge({ produto }: { produto?: string | null }) {
  const meta = leadProdutoBadgeMeta(produto);
  if (!meta) return null;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] sm:text-xs whitespace-normal break-words ${meta.badgeClassName}`}
    >
      {meta.label}
    </Badge>
  );
}

function LeadCard({ lead, index, onDoubleClick, isDragDisabled }: { lead: ExtendedLead; index: number; onDoubleClick: (lead: ExtendedLead) => void; isDragDisabled?: boolean }) {
  const isMobile = useIsMobile();

  const indicadorTitle =
    (lead.tarefaStatus || 'verde') === 'vermelho'
      ? 'Pendências em atraso (tarefas ou próximo contato agendado)'
      : (lead.tarefaStatus || 'verde') === 'amarelo'
        ? 'Pendências em aberto (tarefas ou próximo contato agendado)'
        : 'Sem pendências relevantes';
  
  // Determinar cor do indicador baseado no status das tarefas
  const getStatusColor = () => {
    const status = lead.tarefaStatus || 'verde';
    switch (status) {
      case 'vermelho':
        return 'bg-red-500';
      case 'amarelo':
        return 'bg-yellow-500';
      case 'verde':
        return 'bg-green-500';
      default:
        return 'bg-green-500';
    }
  };

  // Only allow dragging if lead has a real database ID and drag is not disabled
  if (!lead.id || isDragDisabled) {
    return (
      <div 
        className="bg-card border border-border rounded-lg p-2 sm:p-3 mb-2 shadow-sm cursor-pointer"
        onClick={() => onDoubleClick(lead)}
      >
        <div className="flex items-start justify-between mb-1 sm:mb-2 gap-2 min-w-0">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
            {lead.tipo === 'fisica' ? <User className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0" /> : <Building className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />}
            <h3 className="font-medium text-xs sm:text-sm text-foreground whitespace-normal break-words min-w-0 flex-1">{lead.nome || lead.razaoSocial}</h3>
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor()}`} title={indicadorTitle} />
          </div>
          <Badge variant="secondary" className="text-[10px] sm:text-xs flex-shrink-0">
            {lead.tipo === 'fisica' ? 'PF' : 'PJ'}
          </Badge>
        </div>
        {!isMobile && (
          <div className="space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              <span>{formatPhone(lead.telefone)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              <span>{lead.email}</span>
            </div>
            {lead.fatura_urls && lead.fatura_urls.length > 0 && (
              <div className="flex items-center gap-1">
                <FileText className="h-3 w-3" />
                <span>Faturas: {lead.fatura_urls.length}</span>
              </div>
            )}
          </div>
        )}
        {(lead.categoria || lead.temperatura || leadProdutoBadgeMeta(lead.produto)) && (
          <div className="mt-1 sm:mt-2 flex flex-wrap gap-1">
            {lead.categoria && (
              <Badge variant="outline" className="text-[10px] sm:text-xs whitespace-normal break-words">
                {lead.categoria}
              </Badge>
            )}
            {lead.temperatura && (
              <Badge 
                variant="outline" 
                className={`text-[10px] sm:text-xs whitespace-normal break-words ${
                  lead.temperatura === 'Quente' ? 'bg-red-100 text-red-800 border-red-300' :
                  lead.temperatura === 'Morno' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                  'bg-blue-100 text-blue-800 border-blue-300'
                }`}
              >
                {lead.temperatura === 'Quente' && '🔥 '}
                {lead.temperatura === 'Morno' && '🌡️ '}
                {lead.temperatura === 'Frio' && '❄️ '}
                {lead.temperatura}
              </Badge>
            )}
            <LeadProdutoBadge produto={lead.produto} />
          </div>
        )}
      </div>
    );
  }
  
  return (
    <Draggable draggableId={lead.id} index={index} isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onTouchEnd={(e) => {
            // Em mobile, usar toque simples para abrir
            if (isMobile && !snapshot.isDragging) {
              e.preventDefault();
              onDoubleClick(lead);
            }
          }}
          onDoubleClick={(e) => {
            // Em desktop, manter duplo clique
            if (!isMobile) {
              e.stopPropagation();
              onDoubleClick(lead);
            }
          }}
          className={`bg-card border border-border rounded-lg p-2 sm:p-3 mb-2 shadow-sm hover:shadow-md transition-shadow ${
            isDragDisabled ? 'cursor-default' : 'cursor-pointer'
          } ${
            snapshot.isDragging ? 'rotate-2 shadow-lg' : ''
          }`}
        >
          <div className="flex items-start justify-between mb-1 sm:mb-2 gap-2 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
              {lead.tipo === 'fisica' ? <User className="h-3 w-3 sm:h-4 sm:w-4 text-blue-500 flex-shrink-0" /> : <Building className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />}
              <h3 className="font-medium text-xs sm:text-sm text-foreground whitespace-normal break-words min-w-0 flex-1">{lead.nome || lead.razaoSocial}</h3>
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor()}`} title={indicadorTitle} />
            </div>
            <Badge variant="secondary" className="text-[10px] sm:text-xs flex-shrink-0">
              {lead.tipo === 'fisica' ? 'PF' : 'PJ'}
            </Badge>
          </div>
          {!isMobile && (
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                <span>{formatPhone(lead.telefone)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                <span>{lead.email}</span>
              </div>
              {lead.fatura_urls && lead.fatura_urls.length > 0 && (
                <div className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  <span>Faturas: {lead.fatura_urls.length}</span>
                </div>
              )}
            </div>
          )}
          {(lead.categoria || lead.temperatura || leadProdutoBadgeMeta(lead.produto)) && (
            <div className="mt-1 sm:mt-2 flex flex-wrap gap-1">
              {lead.categoria && (
                <Badge variant="outline" className="text-[10px] sm:text-xs whitespace-normal break-words">
                  {lead.categoria}
                </Badge>
              )}
              {lead.temperatura && (
                <Badge 
                  variant="outline" 
                  className={`text-[10px] sm:text-xs whitespace-normal break-words ${
                    lead.temperatura === 'Quente' ? 'bg-red-100 text-red-800 border-red-300' :
                    lead.temperatura === 'Morno' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                    'bg-blue-100 text-blue-800 border-blue-300'
                  }`}
                >
                  {lead.temperatura === 'Quente' && '🔥 '}
                  {lead.temperatura === 'Morno' && '🌡️ '}
                  {lead.temperatura === 'Frio' && '❄️ '}
                  {lead.temperatura}
                </Badge>
              )}
              <LeadProdutoBadge produto={lead.produto} />
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

function AddLeadDialog({
  onUpdate,
  isMobile,
  crmFunil,
}: {
  onUpdate: () => Promise<void>;
  isMobile?: boolean;
  crmFunil: CrmFunilTipo;
}) {
  const [open, setOpen] = useState(false);
  const isParceiro = crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={isMobile ? "icon" : "sm"} className={isMobile ? "rounded-full w-10 h-10" : "gap-2"}>
          <Plus className={isMobile ? "h-5 w-5" : "h-4 w-4 mr-2"} />
          {!isMobile && (isParceiro ? 'Novo contato' : 'Novo Lead')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isParceiro ? 'Registrar prospecção de parceria' : 'Adicionar Novo Lead'}</DialogTitle>
          <DialogDescription>
            {isParceiro
              ? 'Cadastro exclusivo do funil CRM Parceiros — não aparece na página Clientes.'
              : 'Preencha as informações do novo lead que será adicionado ao sistema.'}
          </DialogDescription>
        </DialogHeader>
        <AdicionarLead
          crmFunil={crmFunil}
          onClose={() => setOpen(false)}
          onSuccess={() => {
            void onUpdate();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

type FunilEtapasConfigDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  baseDefs: ColumnDef[];
  leadCountsByEtapa: Record<string, number>;
  onSaved: (defs: ColumnDef[]) => void;
  crmFunil: CrmFunilTipo;
};

function FunilEtapasConfigDialog({
  open,
  onOpenChange,
  baseDefs,
  leadCountsByEtapa,
  onSaved,
  crmFunil,
}: FunilEtapasConfigDialogProps) {
  const [draft, setDraft] = useState<ColumnDef[]>([]);
  const [reassignments, setReassignments] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [reassignOpen, setReassignOpen] = useState<{ removeId: string; count: number; targetId: string } | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(baseDefs.map((c) => ({ ...c })));
      setReassignments({});
    }
  }, [open, baseDefs]);

  const moveRow = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= draft.length) return;
    setDraft((rows) => {
      const next = [...rows];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const applyRemove = (removeId: string, moveLeadsToId?: string) => {
    const n = leadCountsByEtapa[removeId] || 0;
    if (n > 0 && !moveLeadsToId) return false;
    if (n > 0 && moveLeadsToId) {
      setReassignments((prev) => ({ ...prev, [removeId]: moveLeadsToId }));
    } else {
      setReassignments((prev) => {
        const next = { ...prev };
        delete next[removeId];
        return next;
      });
    }
    setDraft((d) => d.filter((x) => x.id !== removeId));
    return true;
  };

  const requestRemove = (removeId: string) => {
    if (draft.length <= 1) {
      toast({ title: 'Não é possível remover', description: 'O funil precisa ter ao menos uma etapa.', variant: 'destructive' });
      return;
    }
    const n = leadCountsByEtapa[removeId] || 0;
    if (n > 0) {
      const firstOther = draft.find((x) => x.id !== removeId)?.id || '';
      setReassignOpen({ removeId, count: n, targetId: firstOther });
      return;
    }
    applyRemove(removeId);
  };

  const confirmReassignRemove = () => {
    if (!reassignOpen) return;
    const { removeId, targetId } = reassignOpen;
    if (!targetId || targetId === removeId) {
      toast({ title: 'Selecione etapa de destino', variant: 'destructive' });
      return;
    }
    if (applyRemove(removeId, targetId)) {
      setReassignOpen(null);
    }
  };

  const handleSave = async () => {
    if (draft.some((r) => !r.title.trim())) {
      toast({ title: 'Títulos obrigatórios', description: 'Preencha o nome de todas as etapas.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const etapasPayload = draft.map((c, i) => ({
        id: c.id,
        titulo: c.title.trim(),
        ordem: i + 1,
        color: c.color,
        customBgColor: c.customBgColor,
        categories: c.categories,
      }));
      const res = await fetchWithAuth('/api/crm-funil/etapas', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ etapas: etapasPayload, reassignments, crmFunil }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Erro ao salvar (${res.status})`);
      }
      if (Array.isArray(data.etapas)) {
        const sorted = [...data.etapas].sort((a: CrmFunilEtapaApi, b: CrmFunilEtapaApi) => a.ordem - b.ordem);
        onSaved(sorted.map(apiEtapaToColDef));
      } else {
        onSaved(draft);
      }
      toast({ title: 'Funil atualizado', description: 'As etapas foram salvas com sucesso.' });
      onOpenChange(false);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao salvar o funil';
      toast({ title: 'Erro ao salvar', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO
                ? 'Configurar etapas do funil Parceiros'
                : 'Configurar etapas do funil'}
            </DialogTitle>
            <DialogDescription>
              {crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO
                ? 'Etapas exclusivas da prospecção de parcerias. Remover etapa com registros exige escolher destino.'
                : 'Altere o nome, a ordem ou exclua etapas. Novas etapas entram no funil e podem receber leads por arrastar e soltar. Se remover uma etapa com leads, é necessário escolher para qual etapa movê-los.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {draft.map((row, index) => (
              <div key={row.id} className="flex flex-col gap-2 rounded-md border border-border p-2 sm:flex-row sm:items-center">
                <div className="flex flex-1 flex-col gap-1">
                  <Label className="text-xs text-muted-foreground">Etapa {index + 1}</Label>
                  <Input
                    value={row.title}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      setDraft((d) => d.map((x) => (x.id === row.id ? { ...x, title: v } : x)));
                    }}
                    placeholder="Nome da etapa"
                  />
                  <p className="text-[10px] text-muted-foreground font-mono">id: {row.id}</p>
                </div>
                <div className="flex flex-row gap-1 sm:flex-col">
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => moveRow(index, -1)} disabled={index === 0} title="Mover para cima">
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="icon" className="h-8 w-8" onClick={() => moveRow(index, 1)} disabled={index >= draft.length - 1} title="Mover para baixo">
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="destructive" size="icon" className="h-8 w-8" onClick={() => requestRemove(row.id)} title="Remover etapa">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() =>
                setDraft((d) => [
                  ...d,
                  {
                    id: newEtapaSlug(),
                    title: 'Nova etapa',
                    color: 'bg-slate-200 text-slate-800',
                  },
                ])
              }
            >
              Adicionar etapa
            </Button>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reassignOpen} onOpenChange={(v) => !v && setReassignOpen(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Mover leads antes de remover</DialogTitle>
            <DialogDescription>
              Esta etapa tem {reassignOpen?.count ?? 0} lead(s). Escolha a etapa de destino.
            </DialogDescription>
          </DialogHeader>
          {reassignOpen && (
            <div className="py-2">
              <Label>Etapas de destino</Label>
              <Select
                value={reassignOpen.targetId}
                onValueChange={(v) => setReassignOpen((s) => (s ? { ...s, targetId: v } : s))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {draft
                    .filter((x) => x.id !== reassignOpen.removeId)
                    .map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        {x.title || x.id}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setReassignOpen(null)}>
              Voltar
            </Button>
            <Button type="button" onClick={confirmReassignRemove}>
              Remover etapa e mover leads
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export type PipelineWidgetProps = {
  crmFunil?: CrmFunilTipo;
};

export function PipelineWidget({ crmFunil = CRM_FUNIL_VENDAS }: PipelineWidgetProps) {
  const defaultEtapas =
    crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO ? DEFAULT_FUNIL_ETAPAS_PARCERIA_API : DEFAULT_FUNIL_ETAPAS_API;
  const defaultEtapaKey =
    crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO ? 'parceiro-prospeccao-inicial' : 'lead-capturado';
  const isParceiroPipeline = crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO;

  const [colDefs, setColDefs] = useState<ColumnDef[]>(() => defaultEtapas.map(apiEtapaToColDef));
  const [columns, setColumns] = useState<Column[]>(() =>
    defaultEtapas.map(apiEtapaToColDef).map((c) => ({ ...c, leads: [] }))
  );
  const [funilConfigOpen, setFunilConfigOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<ExtendedLead | null>(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  
  // Obter o papel e perfil do usuário atual
  const { userRole, userProfile } = useAuthorization();
  const isMobile = useIsMobile();
  
  /** Parceiro que possui rede de parceiros (pode_criar_rede_parceiros) — pode ver leads da rede, sem editar */
  const isParceiroPai = userRole === 'parceiro' && (userProfile?.pode_criar_rede_parceiros === true);
  /** True se o lead pertence a um parceiro da rede (não ao usuário logado). Parceiro pai só visualiza. */
  const isLeadFromRede = (lead: { parceiro_id?: string | null } | null) =>
    !!lead && isParceiroPai && lead.parceiro_id !== userProfile?.id;
  
  // Parceiros não podem mover leads entre etapas, apenas visualizar e criar
  const canMoveLeads = userRole !== 'parceiro';
  const canEditFunil = canMoveLeads && (userRole === 'vendedor' || userRole === 'admin');

  const fetchFunilEtapas = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/crm-funil/etapas?crm_funil=${encodeURIComponent(crmFunil)}`);
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data.etapas) && data.etapas.length > 0) {
        const sorted = [...data.etapas].sort((a: CrmFunilEtapaApi, b: CrmFunilEtapaApi) => a.ordem - b.ordem);
        setColDefs(sorted.map(apiEtapaToColDef));
      }
    } catch (error) {
      console.error('Erro ao carregar definição do funil:', error);
    }
  }, [crmFunil]);

  const fetchLeadsAndGroup = useCallback(async () => {
    console.log('Fetching leads from backend...');
    try {
      const response = await fetchWithAuth(`/api/leads?crm_funil=${encodeURIComponent(crmFunil)}`);
      if (!response.ok) throw new Error('Erro ao buscar leads');
      const data: ExtendedLead[] = await response.json();
      console.log('Fetched leads:', data);
      
      // Filtrar apenas leads (excluir clientes) - O gráfico de campanhas deve mostrar apenas leads
      // Clientes (status = 'Cliente') não devem aparecer no pipeline CRM Sun7
      const leadsOnly = data.filter((lead) => lead.status !== 'Cliente');
      console.log(`📊 Total de leads: ${data.length}, Leads no funil (excl. clientes): ${leadsOnly.length}`);
      
      // Garantir que todos os leads tenham uma etapa válida
      const leadsWithEtapa = leadsOnly.map((lead) => ({
        ...lead,
        etapa: lead.etapa || defaultEtapaKey,
      }));
      
      const defs = colDefs;
      const knownIds = new Set(defs.map((c) => c.id));
      const baseColumns: Column[] = defs.map((col) => ({
        ...col,
        leads: leadsWithEtapa.filter((lead) => lead.etapa === col.id),
      }));
      const orphanIds = [
        ...new Set(
          leadsWithEtapa.map((l) => l.etapa).filter((e): e is string => !!e && !knownIds.has(e))
        ),
      ];
      const orphanColumns: Column[] = orphanIds.map((id) => ({
        id,
        title: `Etapa (cadastro): ${id}`,
        leads: leadsWithEtapa.filter((l) => l.etapa === id),
        color: 'bg-amber-100 text-amber-900',
      }));
      const updatedColumns = [...baseColumns, ...orphanColumns];
      console.log('Updated columns:', updatedColumns);
      setColumns(updatedColumns);
    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    }
  }, [colDefs, crmFunil, defaultEtapaKey]);

  useEffect(() => {
    void fetchFunilEtapas();
  }, [fetchFunilEtapas]);

  useEffect(() => {
    console.log('useEffect called - fetching leads');
    void fetchLeadsAndGroup();
    
    // Atualizar leads quando uma tarefa é concluída
    const handleTarefaConcluida = (event: CustomEvent) => {
      console.log('Tarefa concluída - refreshing leads', event.detail);
      setTimeout(() => {
        void fetchLeadsAndGroup();
      }, 500);
    };
    
    // Refresh de segurança a cada 2 min (evita depender só do gatilho da mudança de etapa)
    const intervalId = setInterval(() => {
      void fetchLeadsAndGroup();
    }, 120000);
    
    window.addEventListener('tarefaConcluida', handleTarefaConcluida as EventListener);
    
    return () => {
      window.removeEventListener('tarefaConcluida', handleTarefaConcluida as EventListener);
      clearInterval(intervalId);
    };
  }, [fetchLeadsAndGroup]);

  const handleDragEnd = async (result: DropResult) => {
    // Verificar se o usuário tem permissão para mover leads
    if (!canMoveLeads) {
      console.log('Usuário não tem permissão para mover leads');
      alert('Você não tem permissão para mover leads entre as etapas. Apenas visualização é permitida.');
      return;
    }
    
    const { source, destination } = result;
    console.log('Drag end result:', result);
    
    // Validações básicas
    if (!destination) {
      console.log('No destination, drag cancelled');
      return;
    }
    
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('Same position, no change needed');
      return;
    }

    // Evitar múltiplas chamadas simultâneas
    if (isUpdating) {
      console.log('Update already in progress, ignoring drag');
      return;
    }

    // Encontra o lead que foi movido
    const sourceColumn = columns.find(col => col.id === source.droppableId);
    if (!sourceColumn) {
      console.log('Source column not found:', source.droppableId);
      return;
    }
    
    const movedLead = sourceColumn.leads[source.index];
    console.log('Moved lead:', movedLead);
    if (!movedLead || !movedLead.id) {
      console.log('Invalid lead or missing ID');
      return;
    }

    const sourceOrd = colDefs.find((c) => c.id === source.droppableId)?.ordem;
    const destOrd = colDefs.find((c) => c.id === destination.droppableId)?.ordem;
    if (
      crmFunil === CRM_FUNIL_VENDAS &&
      isProdutoLeadSemProduto(movedLead.produto) &&
      typeof sourceOrd === 'number' &&
      typeof destOrd === 'number' &&
      destOrd > sourceOrd &&
      destination.droppableId !== 'leads-perdidos'
    ) {
      toast({
        variant: 'destructive',
        title: 'Produto não definido',
        description:
          'Defina o produto deste lead nas informações do cadastro antes de avançar para a próxima etapa do funil.',
      });
      return;
    }

    // Salva o estado anterior para rollback em caso de erro
    const previousColumns = JSON.parse(JSON.stringify(columns));

    // Atualiza estado local otimisticamente
    console.log('Updating local state optimistically...');
    setColumns(prevColumns => {
      const updated = [...prevColumns];
      const sourceIdx = updated.findIndex(col => col.id === source.droppableId);
      const destIdx = updated.findIndex(col => col.id === destination.droppableId);
      
      if (sourceIdx === -1 || destIdx === -1) {
        console.log('Column indices not found');
        return prevColumns;
      }
      
      const [moved] = updated[sourceIdx].leads.splice(source.index, 1);
      moved.etapa = destination.droppableId;
      updated[destIdx].leads.splice(destination.index, 0, moved);
      
      console.log('Updated columns:', updated);
      return updated;
    });

    // Marca como atualizando
    setIsUpdating(true);

    // Atualiza backend
    try {
      const etapaValue = destination.droppableId;
      console.log(`Updating lead ${movedLead.id} to stage ${etapaValue}`);
      console.log('📤 Sending to backend:', {
        etapa: etapaValue,
        type: typeof etapaValue,
        length: etapaValue?.length,
        charCodes: etapaValue ? Array.from(etapaValue).map(c => c.charCodeAt(0)) : []
      });
      const response = await fetchWithAuth(`/api/leads/${movedLead.id}/etapa`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ etapa: etapaValue }),
      });
      
      console.log('API response status:', response.status);
      
      if (!response.ok) {
        let errorMessage = 'Erro ao atualizar etapa';
        try {
          const errorData = await response.json();
          console.log('API response not ok, error:', errorData);
          
          // Se a API retornou uma mensagem de erro estruturada, usar ela
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
          
          // Se há informações sobre tarefas pendentes, formatar melhor
          if (errorData.tarefas_pendentes && Array.isArray(errorData.tarefas_pendentes)) {
            const sourceColumn = columns.find(col => col.id === source.droppableId);
            const etapaNome = sourceColumn?.title || 'atual';
            
            let mensagemFormatada = `Não é possível avançar o lead. É obrigatório completar todas as tarefas da etapa "${etapaNome}" antes de avançar.\n\n`;
            mensagemFormatada += `Tarefas pendentes:\n`;
            
            errorData.tarefas_pendentes.forEach((item: { ordem: number; tarefas: string[] }) => {
              item.tarefas.forEach((tarefa: string) => {
                mensagemFormatada += `• Ordem ${item.ordem}: ${tarefa}\n`;
              });
            });
            
            mensagemFormatada += `\nPor favor, finalize todas as tarefas pendentes antes de avançar o lead.`;
            errorMessage = mensagemFormatada;
          }
        } catch (e) {
          // Se não conseguir parsear JSON, tentar como texto
          const errorText = await response.text();
          errorMessage = errorText || `Erro ao atualizar etapa: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      
      const responseData = await response.json();
      console.log('API call successful:', responseData);
      
      // Atualiza o lead com os dados retornados do backend (incluindo tarefaStatus para o indicador atualizar na hora)
      const leadFromApi = responseData.lead || {};
      const newTarefaStatus = leadFromApi.tarefaStatus;
      setColumns(prevColumns => {
        const updated = prevColumns.map(col => ({ ...col, leads: [...col.leads] }));
        const destIdx = updated.findIndex(col => col.id === destination.droppableId);
        if (destIdx !== -1) {
          const leadIndex = updated[destIdx].leads.findIndex(lead => lead.id === movedLead.id);
          if (leadIndex !== -1) {
            const currentLead = updated[destIdx].leads[leadIndex];
            updated[destIdx].leads[leadIndex] = {
              ...currentLead,
              ...leadFromApi,
              // Garante que o indicador (ponto) use o status da nova etapa
              tarefaStatus: newTarefaStatus !== undefined ? newTarefaStatus : currentLead.tarefaStatus,
            };
          }
        }
        return updated;
      });
      
    } catch (error) {
      console.error('Erro ao atualizar etapa no backend:', error);
      
      // Rollback local state to previous state
      console.log('Rolling back local state after error');
      setColumns(previousColumns);
      
      // Mostra erro para o usuário usando alert (pode ser substituído por toast)
      // Formatar mensagem para exibição mais legível
      const mensagemFormatada = error.message
        .replace(/\n/g, '\n')
        .replace(/•/g, '•');
      
      toast({
        variant: 'destructive',
        title: 'Não foi possível mover o lead',
        description: mensagemFormatada,
      });
    } finally {
      // Sempre reseta o estado de loading
      setIsUpdating(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3 sm:pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <CardTitle className="text-lg sm:text-xl font-semibold">
              {isParceiroPipeline ? 'Funil CRM Parceiros (prospecção)' : 'Funil de Vendas'}
            </CardTitle>
            {!isMobile && (
              <div className="flex flex-wrap items-center gap-2">
                <AddLeadDialog crmFunil={crmFunil} onUpdate={fetchLeadsAndGroup} isMobile={false} />
                {canEditFunil && (
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setFunilConfigOpen(true)}>
                    <Settings2 className="h-4 w-4" />
                    Etapas do funil
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-2 sm:gap-4 text-xs`}>
            {isMobile && canEditFunil && (
              <Button type="button" variant="outline" size="sm" className="w-full gap-2" onClick={() => setFunilConfigOpen(true)}>
                <Settings2 className="h-4 w-4" />
                Etapas do funil
              </Button>
            )}
            {!canMoveLeads && (
              <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-800 border-yellow-300 w-fit">
                Modo Visualização - Apenas leitura
              </Badge>
            )}
            {!isParceiroPipeline && (
              <div className={`flex ${isMobile ? 'flex-wrap' : 'flex-row'} items-center gap-2`}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-muted-foreground text-[10px] sm:text-xs">Tarefas atrasadas</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-yellow-500" />
                  <span className="text-muted-foreground text-[10px] sm:text-xs">Tarefas pendentes</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-muted-foreground text-[10px] sm:text-xs">Sem tarefas pendentes</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="relative flex overflow-x-auto gap-3 sm:gap-4 pb-4 scroll-smooth">
            {/* Gradientes para indicar scroll */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-r from-transparent to-background pointer-events-none z-10" />
            
            {columns.map(col => (
              <div 
                key={col.id} 
                className={`rounded-lg p-2 sm:p-3 min-h-[400px] w-[280px] sm:w-[300px] md:w-[242px] lg:w-[280px] flex-shrink-0 overflow-x-hidden ${col.color}`}
                style={col.customBgColor ? { backgroundColor: col.customBgColor } : undefined}
              >
                <h3 className="font-semibold mb-2 text-xs sm:text-sm text-foreground min-h-[50px] sm:min-h-[60px] flex items-center justify-center text-center whitespace-normal break-words px-1">
                  {col.title}
                  <Badge variant="secondary" className="ml-2 text-[10px] sm:text-xs">
                    {col.leads.length}
                  </Badge>
                </h3>
                <Droppable droppableId={col.id}>
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="min-h-[350px] max-h-[600px] sm:max-h-[700px] overflow-y-auto p-1 sm:p-2 border border-dashed border-gray-300 rounded-md"
                    >
                      {col.leads.length === 0 ? (
                        <p className="text-center text-muted-foreground text-xs sm:text-sm mt-20">Nenhum lead aqui</p>
                      ) : (
                        col.leads.map((lead, index) => (
                          <LeadCard 
                            key={lead.id} 
                            lead={lead} 
                            index={index}
                            isDragDisabled={!canMoveLeads}
                            onDoubleClick={(lead) => {
                              setSelectedLead(lead);
                              setDetailOpen(true);
                            }}
                          />
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      </CardContent>
      
      {/* Botão flutuante para mobile */}
      {isMobile && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button 
            size="lg" 
            className="rounded-full w-14 h-14 shadow-lg"
            onClick={() => setAddLeadOpen(true)}
          >
            <Plus className="h-6 w-6" />
          </Button>
        </div>
      )}
      
      {/* Dialog para adicionar lead (mobile) */}
      {isMobile && (
        <Dialog open={addLeadOpen} onOpenChange={setAddLeadOpen}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto p-3 sm:p-6">
            <DialogHeader>
              <DialogTitle>
                {isParceiroPipeline ? 'Registrar prospecção de parceria' : 'Adicionar Novo Lead'}
              </DialogTitle>
              <DialogDescription>
                {isParceiroPipeline
                  ? 'Cadastro exclusivo do funil CRM Parceiros — não aparece na página Clientes.'
                  : 'Preencha as informações do novo lead que será adicionado ao sistema.'}
              </DialogDescription>
            </DialogHeader>
            <AdicionarLead
              crmFunil={crmFunil}
              onClose={() => setAddLeadOpen(false)}
              onSuccess={() => {
                setAddLeadOpen(false);
                void fetchLeadsAndGroup();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] md:max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {isParceiroPipeline || isProdutoLeadParceiroCrm(selectedLead.produto)
                    ? `Prospecção de parceria: ${selectedLead.nome || selectedLead.razaoSocial}`
                    : `Dados do Cliente: ${selectedLead.nome || selectedLead.razaoSocial}`}
                </DialogTitle>
                <DialogDescription>
                  {isParceiroPipeline || isProdutoLeadParceiroCrm(selectedLead.produto)
                    ? 'Registro de contatos e dados da organização prospectada (funil parceiros).'
                    : isProdutoLeadCarregador(selectedLead.produto)
                      ? 'Visualize e gerencie as informações do cliente, incluindo dados gerais, fotos, documentos e contratos.'
                      : 'Visualize e gerencie todas as informações, faturas, projetos e documentos do cliente.'}
                </DialogDescription>
              </DialogHeader>
              <div className="[&>div:first-child]:hidden">
                <ClientDetails
                  cliente={selectedLead} 
                  onClose={() => setDetailOpen(false)} 
                  onUpdate={fetchLeadsAndGroup}
                  readOnly={isLeadFromRede(selectedLead)}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <FunilEtapasConfigDialog
        open={funilConfigOpen}
        onOpenChange={setFunilConfigOpen}
        baseDefs={colDefs}
        crmFunil={crmFunil}
        leadCountsByEtapa={Object.fromEntries(
          colDefs.map((d) => {
            const col = columns.find((c) => c.id === d.id);
            return [d.id, col?.leads.length ?? 0];
          })
        )}
        onSaved={(defs) => {
          setColDefs(defs);
          void fetchLeadsAndGroup();
        }}
      />
    </Card>
  );
}
